import { type TElementSpecifier, toJQuery } from "@de-xima/xima-common-js-dom";
import { notNullish } from "@de-xima/xima-common-js-lang";
import jQuery from "jquery";
import { debounce } from "lodash-es";

import { findWidgetForElement, getWidgetByVar } from "./Util.ts";

const DebouncedGlobalFilter = new Map<string, () => void>();

// Function for primefaces datatable widget.

/**
 * @param element Either the datatable element (or an element within the datatable); or the widget variable of the datatable.
 * @returns The data table widget if it exists.
 */
function findDataTable(element: TElementSpecifier<HTMLElement>): PrimeFaces.widget.DataTable | undefined {
  let widget: PrimeFaces.widget.DataTable | undefined;
  if (typeof element === "string") {
    widget = getWidgetByVar(element, PrimeFaces.widget.DataTable);
  }
  if (widget === undefined) {
    const $element = toJQuery(element);
    widget = findWidgetForElement($element.closest(".ui-datatable"), PrimeFaces.widget.DataTable);
  }
  return widget;
}

/**
 * Moves to the last page of the given `<p:datatable/>` and puts focus on the
 * last row.
 * @param element Either the datatable element (or an element within the
 * datatable); or the widget variable of the datatable.
 */
export function showAndFocusLastRow(element: TElementSpecifier<HTMLElement>): void {
  const widget = findDataTable(element);
  if (widget === undefined) {
    console.warn("No datatable widget found for element", element);
    return;
  }
  const paginator = widget.getPaginator();
  if (paginator !== undefined) {
    paginator.setPage((paginator.cfg.pageCount ?? 0) - 1);
  }
  widget.jq.one("page", () => {
    const lastRow = widget.getTbody().find(widget.rowSelector).last();
    jQuery(lastRow).find("input,select").first().trigger("focus");
  });
}

/**
 * Returns a debounced function for the global filter of a datatable.
 * The specified delay is used only the first time this function is called
 * for a certain widget variable.
 * @param widgetVar Widget variable of the datatable.
 * @param waitTime The number of milliseconds to delay. Defaults to 500.
 * @returns The debounced filter.
 */
export function getDebouncedGlobalFilter(widgetVar: string, waitTime: number): (text: string) => void {
  const fn = DebouncedGlobalFilter.get(widgetVar);
  if (fn === undefined) {
    const debounced = debounce(() => {
      const dataTable = getWidgetByVar(widgetVar, PrimeFaces.widget.DataTable);
      if (dataTable !== undefined) {
        dataTable.filter();
      }
    }, waitTime ?? 500);
    DebouncedGlobalFilter.set(widgetVar, debounced);
    return debounced;
  }
  return fn;
}

/**
 * Clears all filters of the data table
 * @param widgetVar Widget variable name of the data table
 */
export function clearFilters(widgetVar: string): void {
  const table = getWidgetByVar(widgetVar, PrimeFaces.widget.DataTable);
  if (notNullish(table)) {
    const filterInputs = table.jq.find(".ui-datatable-scrollable-header").find(":input");
    for (const filterInput of filterInputs) {
      jQuery(filterInput).val("");
    }
    for (const inputNumber of table.jq.find(".ui-inputnumber")) {
      const widget = findWidgetForElement(inputNumber, PrimeFaces.widget.InputNumber);
      if (widget !== undefined) {
        widget.setValue("");
      }
    }
  }
}
